import { supa } from "./supabase";

// Error de RPC con la info suficiente para MOSTRARLA en pantalla.
// En la app actual el patrón es `if (error) { console.error(error); return; }`
// — la vista se queda con datos viejos y nadie se entera. Aquí cualquier fallo
// sube como excepción para que la vista pinte un aviso visible.
export class RpcError extends Error {
  readonly fn: string;
  readonly detalle?: string;

  constructor(fn: string, mensaje: string, detalle?: string) {
    super(mensaje);
    this.name = "RpcError";
    this.fn = fn;
    this.detalle = detalle;
  }
}

// Códigos con los que Postgres/PostgREST avisan que YA NO HAY SESIÓN válida:
// PGRST301 = el JWT venció; 42501 = llegamos como anon y el RPC no le toca.
// auth-js tarda en darse cuenta (renueva el token en segundo plano), así que
// hay ~1 minuto en el que todo truena mientras la app sigue pintando el
// tablero: un muro de tarjetas de error con un "Reintentar" que no puede
// funcionar. Aquí se le adelanta el cierre de sesión.
const CODIGOS_SIN_SESION = new Set(["PGRST301", "42501"]);

export const TEXTO_SESION_VENCIDA = "Tu sesión venció. Vuelve a entrar.";

// La nota que explica por qué la persona acabó en el login. Va por
// sessionStorage porque el aviso se genera cuando la vista que consultaba ya
// se está desmontando: si se dejara en el estado de esa vista, nadie lo vería
// y la salida parecería espontánea.
const BANDERA_SESION_VENCIDA = "radar_sesion_vencida";

/** Lee y BORRA la nota de sesión vencida (para pintarla una sola vez). */
export function tomarAvisoSesionVencida(): string | null {
  try {
    const hay = sessionStorage.getItem(BANDERA_SESION_VENCIDA);
    if (!hay) return null;
    sessionStorage.removeItem(BANDERA_SESION_VENCIDA);
    return TEXTO_SESION_VENCIDA;
  } catch {
    // Safari en privado puede negar sessionStorage: sin nota, pero sin romper.
    return null;
  }
}

/**
 * ¿Este error significa que ya no hay sesión válida? Sirve tanto para errores
 * de PostgREST (traen `code`) como para respuestas crudas de Edge Functions,
 * donde un JWT vencido llega como HTTP 401 (el 403 NO cuenta: ahí el token
 * sirve y lo que falta es el rol).
 */
export function esSesionVencida(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: unknown; status?: unknown };
  if (typeof e.code === "string" && CODIGOS_SIN_SESION.has(e.code)) return true;
  return e.status === 401;
}

// Una sola salida aunque tronen diez consultas al mismo tiempo. Se vuelve a
// armar al terminar: si la persona vuelve a entrar en la misma pestaña, su
// próxima expiración también tiene que cerrar sesión.
let cierreEnCurso: Promise<unknown> | null = null;
async function cerrarSesionVencida() {
  try {
    sessionStorage.setItem(BANDERA_SESION_VENCIDA, "1");
  } catch {
    // sin sessionStorage se pierde el aviso, no el cierre de sesión
  }
  cierreEnCurso ??= supa.auth.signOut().finally(() => {
    cierreEnCurso = null;
  });
  try {
    await cierreEnCurso;
  } catch {
    // Aunque el POST de logout falle (sin red), auth-js ya borró la sesión
    // local y emitió SIGNED_OUT: la app cae al login igual.
  }
}

/**
 * Guardia para las ESCRITURAS, que no pasan por rpc(): si el error es de
 * sesión vencida cierra la sesión (y deja la nota para el login) y devuelve
 * true, para que quien llama lance el aviso correcto en vez de rotularlo como
 * un fallo al guardar. Se espera al signOut a propósito, igual que en rpc().
 */
export async function forzarCierreSiVencio(error: unknown): Promise<boolean> {
  if (!esSesionVencida(error)) return false;
  await cerrarSesionVencida();
  return true;
}

/** Llama un RPC api_* de Supabase y devuelve su payload tipado. */
export async function rpc<T>(
  fn: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await supa.rpc(fn, params);
  if (error) {
    // Se ESPERA al signOut a propósito: cuando esto lanza, App ya cambió al
    // login y la vista que consultaba está desmontada — nadie llega a ver una
    // tarjeta de error de un tablero que ya no existe.
    if (await forzarCierreSiVencio(error)) {
      throw new RpcError(fn, TEXTO_SESION_VENCIDA, error.message);
    }
    throw new RpcError(
      fn,
      "No se pudieron cargar los datos.",
      error.message ?? undefined,
    );
  }
  return data as T;
}

/** Clave de caché estable para TanStack Query. */
export const claveRpc = (fn: string, params: Record<string, unknown> = {}) =>
  [fn, params] as const;
